import { Button } from 'primereact/button';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast, ToastMessage } from "primereact/toast";
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from 'primereact/dropdown';
import { userOption } from '../../models/orders/order-list-model';
import orderServices from '../../services/orders/orderservice';
import { Calendar } from 'primereact/calendar';
import { RadioButton } from 'primereact/radiobutton';

interface SearchPanelProps {
  onSearch: (filters: any) => void;
  setLoading: (loading: boolean) => void;
  setNewData: any;
  initialDates?: {
    startDate: string;
    endDate: string;
  };
}

interface ComplaintTypeSelection {
  name: string;
  key: string;
}

const CustomerComplaintSearch: React.FC<SearchPanelProps> = ({
  onSearch,
  setLoading,
  setNewData,
  initialDates
}) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const blankUserDetail: userOption[] = [{ userID: 0, name: 'Not found', primaryContactNbr: '' }];
  const [orderUser, setOrderUser] = useState<any[]>([]);
  const [orderUsers, setOrderUsers] = useState<userOption[]>(blankUserDetail);
  const [dropdownValue, setDropdownValue] = useState<string | null>(null);
  const [selectedUser, setSelectedUserState] = useState<any>(null);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [selectedComplaintType, setSelectedComplaintType] = useState<'product' | 'service'>('product');

  const complaintTypes: ComplaintTypeSelection[] = [
    { name: 'Product Complaints', key: 'product' },
    { name: 'Service Complaints', key: 'service' }
  ];

  const handleSearch = () => {
    const filters: any = {
      customerID: dropdownValue,
      complaintType: selectedComplaintType,
    };

    if (startDate) {
      filters.startDate = new Date(startDate.setHours(0, 0, 0, 0));
    }
    if (endDate) {
      filters.endDate = new Date(endDate.setHours(23, 59, 59, 999));
    }

    onSearch(filters);
  };

  const resetSearchFields = () => {
    setDropdownValue(null);
    setStartDate(null);
    setEndDate(null);
    //setSelectedComplaintType('product');

    onSearch({
      customerID: null,
      complaintType: selectedComplaintType,
      startDate: null,
      endDate: null
    });
  };

  const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
    toast.current?.show({ severity: severity, summary: label, detail: message, life: 3000 });
  };

  useEffect(() => {
    if (orderUsers.length > 0) {
      setOrderUser(orderUsers.map((user) => ({
        userID: user.userID,
        detail: user.userID !== 0 ? `${user.name}, ${user.primaryContactNbr}` : "",
      })));
    }
  }, [orderUsers]);

  const handleDropdownChange = (e: DropdownChangeEvent) => {
    const selectedUserId = e.value;
    const selectedUser = orderUser.find(user => user.userID === selectedUserId);

    if (selectedUser) {
      const [name, primaryContactNbr] = selectedUser.detail.split(",").map((item: string) => item.trim());
      setSelectedUserState({ name, primaryContactNbr });
      setDropdownValue(selectedUserId);
    }
  };

  const handleDropdownFilter = (e: DropdownFilterEvent) => {
    if (e.filter.length > 0) {
      orderServices.getOrderUsers(e.filter).then((response: userOption[]) => {
        if (response.length > 0) {
          setOrderUsers(response);
        }
      });
    }
  };

  return (
    <div className="col-12" style={{ width: "100%", padding: "0px", marginBottom: "0.5rem" }}>
      <Toast ref={toast} />
      <div className="card border-noround p-2" style={{ minHeight: '3.5rem' }}>
        <div className="p-fluid grid p-2">
          {/* Complaint Type Radio Buttons */}
          <div className="col-12 md:col-4 flex align-items-center gap-3">
            {complaintTypes.map((type) => (
              <div key={type.key} className="flex align-items-center">
                <RadioButton
                  inputId={type.key}
                  name="complaintType"
                  value={type.key}
                  onChange={(e) => {
                    setSelectedComplaintType(e.value as 'product' | 'service');
                    const filters: any = {
                      customerID: dropdownValue,
                      complaintType: e.value,
                    };
                    if (startDate) {
                      filters.startDate = new Date(startDate.setHours(0, 0, 0, 0));
                    }
                    if (endDate) {
                      filters.endDate = new Date(endDate.setHours(23, 59, 59, 999));
                    }
                    onSearch(filters);
                  }}
                  checked={selectedComplaintType === type.key}
                />
                <label htmlFor={type.key} className="ml-2">
                  {type.name}
                </label>
              </div>
            ))}
          </div>

          <div className="col-12 sm:col-6 lg:col-3 font-bold text-white border-round"
               style={{ display: 'flex', justifyContent: 'space-between', padding: '5px' }}>
            <Calendar
              value={startDate}
              onChange={(e) => setStartDate(e.value instanceof Date ? e.value : null)}
              dateFormat="dd/mm/yy"
              style={{ width: "50%", marginRight: "10px" }}
              placeholder="Start Date 🗓️"
              showButtonBar
            />

            <Calendar
              value={endDate}
              onChange={(e) => setEndDate(e.value instanceof Date ? e.value : null)}
              dateFormat="dd/mm/yy"
              style={{ width: "50%" }}
              placeholder="End Date 🗓️"
              showButtonBar
            />

          </div>

          <div className="col-12 md:col-4 flex align-items-center gap-3">
            <Button
              className="text-black border-round"
              style={{ width: "30%" }}
              onClick={handleSearch}
              severity="success"
            >
              Filter
            </Button>

            <Button
              className="text-black border-round"
              style={{ width: "30%" }}
              onClick={resetSearchFields}
              severity="secondary"
              text
              raised
            >
              Reset
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerComplaintSearch;
